/*
 * phone-guard.js — anti-tamper protection for phone numbers.
 *
 * Some malicious browser extensions silently rewrite phone numbers (and the
 * tel: links behind them) so calls get routed to a scammer instead of us. This
 * guard re-asserts the canonical number — the value WE control — anywhere it has
 * been tampered with, on load, on DOM mutation, on a timer, and at click time.
 *
 * Declarative usage: mark any element whose text and/or href IS a phone number
 * with a data-pg attribute holding the canonical, formatted value. The canonical
 * lives in the attribute (an obscure name these extensions don't touch), so it
 * survives swaps of the visible text and the href:
 *
 *   Leaf link (text + href guarded):
 *     <a href="tel:+13058489965" data-pg="+1 (305) 848-9965">+1 (305) 848-9965</a>
 *
 *   Link with inner markup (icon + text) — put data-pg on BOTH the <a> (guards
 *   the href) and the inner text element (guards the visible number):
 *     <a href="tel:+13058489965" data-pg="+1 (305) 848-9965">
 *       <span class="icon">…</span>
 *       <span data-pg="+1 (305) 848-9965">+1 (305) 848-9965</span>
 *     </a>
 *
 * Works for elements present at load and for anything injected later (carousels,
 * JS-built cards, the store-locator detail panel, etc.) via a document-wide
 * MutationObserver.
 */
(function () {
  if (window.__phoneGuardActive) return;
  window.__phoneGuardActive = true;

  var SELECTOR = '[data-pg]';

  // Reduce any phone-ish string to comparable digits (keep a leading +).
  var norm = function (s) { return (s || '').replace(/[^0-9+]/g, ''); };
  var digitCount = function (d) { return d.replace(/\+/g, '').length; };

  // Numbers managed by Google Ads website call conversions: gtag config with a
  // phone_conversion_number means Google legitimately swaps that number for a
  // forwarding number on ad-click visits. Guarding those numbers would undo the
  // swap (guard sees it as tampering), killing call tracking — so skip them.
  // On CallRail pages the element holds the store number while the config holds a
  // CallRail number, so a config number from window.kzCallRailNumbers skips the guard too:
  // Google's random forwarding number cannot be told from tampering there.
  function wccManaged(canonical) {
    try {
      var dl = window.dataLayer || [];
      for (var i = 0; i < dl.length; i++) {
        var e = dl[i];
        if (e && e[0] === 'config' && e[2] && e[2].phone_conversion_number) {
          var configured = norm(e[2].phone_conversion_number).slice(-10);
          if (configured === canonical.replace(/\+/g, '').slice(-10) || callRailNumber(configured)) return true;
        }
      }
    } catch (e) {}
    return false;
  }

  // Pages with snippets/callrail-swap (distributor-ppc-light): CallRail swap.js
  // legitimately replaces the store number with a tracking number from
  // window.kzCallRailNumbers. Reverting those would kill call tracking, so they
  // pass; any other number is still tampering. The list is set by an inline script
  // at the end of <body>, which runs before this deferred file.
  function callRailNumber(digits) {
    var list = window.kzCallRailNumbers;
    return Array.isArray(list) && list.indexOf(digits.replace(/\+/g, '').slice(-10)) !== -1;
  }

  function beacon() {
    try {
      navigator.sendBeacon && navigator.sendBeacon(
        '/api/m',
        JSON.stringify({ u: location.href, t: Date.now() })
      );
    } catch (e) {}
  }

  function enforce(el) {
    var display = el.getAttribute('data-pg');
    if (!display) return;
    var canonical = norm(display);
    if (digitCount(canonical) < 7) return; // not a real number — ignore
    if (wccManaged(canonical)) return; // Google call tracking owns this number

    var changed = false;

    // Guard the call target on anchors.
    if (el.tagName === 'A') {
      var hrefDigits = norm((el.getAttribute('href') || '').replace(/^tel:/i, ''));
      if (hrefDigits !== canonical && !callRailNumber(hrefDigits)) {
        el.setAttribute('href', 'tel:' + canonical);
        changed = true;
      }
    }

    // Guard the visible number only on leaf elements whose text currently IS a
    // phone number. This protects "+1 (...)" text from being swapped, while never
    // clobbering labels ("Or call us now") or wrapping markup (icons live in
    // child elements, so those nodes are skipped).
    if (el.children.length === 0) {
      var cur = norm(el.textContent);
      if (digitCount(cur) >= 7 && cur !== canonical && !callRailNumber(cur)) {
        el.textContent = display;
        changed = true;
      }
    }

    if (changed) beacon();
  }

  function scan() {
    var els = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) enforce(els[i]);
  }

  function start() {
    scan();

    try {
      new MutationObserver(scan).observe(document.documentElement, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['href']
      });
    } catch (e) {}

    setInterval(scan, 2000);

    // Last line of defence: if the href is wrong at the moment of the click,
    // fix it and dial the correct number.
    document.addEventListener('click', function (e) {
      var a = (e.target && e.target.closest) ? e.target.closest('a[data-pg]') : null;
      if (!a) return;
      var canonical = norm(a.getAttribute('data-pg'));
      if (digitCount(canonical) < 7) return;
      if (wccManaged(canonical)) return;
      var hrefDigits = norm((a.getAttribute('href') || '').replace(/^tel:/i, ''));
      if (hrefDigits !== canonical && !callRailNumber(hrefDigits)) {
        e.preventDefault();
        a.setAttribute('href', 'tel:' + canonical);
        beacon();
        window.location.href = 'tel:' + canonical;
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
